'use client';

import React, {
  type ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Select,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useConnection, useUser } from '@sezenta/connection';
import { useNavigate } from 'react-router-dom';
import { useBusinessContext } from '../context/BusinessContext';
import { useEvents } from './EventContext';
import { Permission } from '../dto';
import dayjs, { Dayjs } from 'dayjs';

export type CollectionFormModalInstance = {
  open: (data?: any) => void;
};

type Props = {};

type SiteOption = {
  label: string;
  value: string;
};

type ChangedCollectionRow = {
  quantity?: number;
  collectionType?: number;
  weight?: number;
  note?: string;
};

const range = (start: number, end: number) =>
  start > end
    ? []
    : Array.from({ length: end - start + 1 }, (_, i) => i + start);

const CollectionFormModal: ForwardRefRenderFunction<
  CollectionFormModalInstance,
  Props
> = (props, ref) => {
  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        setVisible(true);
      },
    }),
    [],
  );
  const [visible, setVisible] = useState(false);
  const [form] = Form.useForm();
  const connection = useConnection();
  const events = useEvents();
  const user = useUser();
  const navigate = useNavigate();
  const { businessId, select, collectionType, sitesWithIds, getNameById } =
    useBusinessContext();

  const [selectedSite, setSelectedSite] = useState<SiteOption>();
  const [collectionTypes, setCollectionTypes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSiteDetails, setSelectedSiteDetails] = useState<{
    address: string;
    suburb: string;
    state: string;
  } | null>(null);

  // Watch collection field to enable/disable submit button
  const collections = Form.useWatch('collections', form) || [];

  const siteOptions: SiteOption[] = useMemo(
    () => (select?.sites || []) as SiteOption[],
    [select?.sites],
  );

  // When no stores exist for this project, explain why the site list is empty and
  // (for users who can) link to the Stores page to add one, instead of a bare
  // "No data". Navigating closes this modal, which is fine.
  const canCreateSite = user?.permissions.includes(Permission.CREATE_SITE);
  const siteEmptyHint =
    siteOptions.length === 0 ? (
      <div className="py-3 px-2 text-center">
        <div className="text-[13px] text-[#5E6870] mb-1">
          No stores in this project yet.
        </div>
        {canCreateSite ? (
          <a
            onClick={() => {
              setVisible(false);
              navigate(`/portal/${businessId}/stores`);
            }}
            className="text-[12px] font-medium text-[#26A3A7] cursor-pointer"
          >
            Add a store →
          </a>
        ) : (
          <div className="text-[12px] text-[#84919A]">
            Ask an admin to add stores first.
          </div>
        )}
      </div>
    ) : undefined;

  // Collection types are filtered by the selected site's state. Empty means either
  // no site is chosen yet, or none are configured for that state — guide the user
  // accordingly instead of a bare "No data".
  const canConfigureMasterData =
    user?.permissions.includes(Permission.CREATE_MASTER_DATA) ||
    user?.permissions.includes(Permission.EDIT_MASTER_DATA);
  const collectionTypeEmptyHint =
    collectionTypes.length === 0 ? (
      <div className="py-3 px-2 text-center">
        {!selectedSite ? (
          <div className="text-[13px] text-[#5E6870]">
            Select a site first to see its collection types.
          </div>
        ) : (
          <>
            <div className="text-[13px] text-[#5E6870] mb-1">
              No collection types configured for this site&apos;s state.
            </div>
            {canConfigureMasterData ? (
              <a
                onClick={() => {
                  setVisible(false);
                  navigate(`/portal/${businessId}/master-data/CollectionType`);
                }}
                className="text-[12px] font-medium text-[#26A3A7] cursor-pointer"
              >
                Configure collection types →
              </a>
            ) : (
              <div className="text-[12px] text-[#84919A]">
                Ask an admin to add them in Master Data.
              </div>
            )}
          </>
        )}
      </div>
    ) : undefined;

  useImperativeHandle(ref, () => ({
    open: (data?: any) => {
      setVisible(true);

      if (data) {
        form.setFieldsValue({
          siteId: data.siteId,
          createdAt: data.createdAt ? dayjs(data.createdAt) : dayjs(),
          collections: data.collections.map((c: any) => ({
            ...c,
            collectionType: Number(c.collectionType),
            quantity: Number(c.quantity),
            weight: Number(c.weight),
          })),
        });

        const site = siteOptions.find(
          (s) => Number(s.value) === Number(data.siteId),
        );
        setSelectedSite(site);
      } else {
        form.setFieldsValue({ createdAt: dayjs() });
      }
    },
  }));

  useEffect(() => {
    if (selectedSite) {
      const siteData = sitesWithIds[selectedSite.value];
      const state = siteData.state;
      const options = Object.values(collectionType)
        .filter((c: any) => c?.state === state)
        .map((c: any) => {
          return {
            value: c.id,
            label: c.name,
          };
        });
      setCollectionTypes(options);

      console.log(siteData.suburb);

      // Set site details for display
      const suburbName = getNameById('Suburb', siteData?.suburb.toString());
      console.log(suburbName);
      const stateName = getNameById('State', siteData.state.toString());

      setSelectedSiteDetails({
        address: siteData.address || '',
        suburb: suburbName,
        state: stateName,
      });
    } else {
      setSelectedSiteDetails(null);
    }
  }, [collectionType, selectedSite, siteOptions, sitesWithIds, getNameById]);

  const handleCancel = useCallback(() => {
    form.resetFields();
    setSelectedSite(undefined);
    setSelectedSiteDetails(null);
    setVisible(false);
  }, [form]);

  const recalculateWeights = useCallback(() => {
    const values = form.getFieldsValue();
    const rows = values?.collections;

    if (!Array.isArray(rows)) return;

    rows.forEach((row: any, index: number) => {
      if (row?.quantity == null || row?.collectionType == null) return;

      const typeId = Number(row.collectionType);

      const typeData = Object.values(collectionType).find(
        (c: any) => Number(c.id) === typeId,
      );

      if (!typeData) return;

      const newWeight = row.quantity * (typeData.weight ?? 0);
      form.setFieldValue(['collections', index, 'weight'], newWeight);
    });
  }, [collectionType, form]);

  useEffect(() => {
    if (visible && collectionTypes.length > 0) {
      recalculateWeights();
    }
  }, [visible, collectionTypes, recalculateWeights]);

  const handleChangeValues = useCallback(
    (changedValues: any, allValues: any) => {
      const changedCollections = changedValues?.collections;
      const allCollections = allValues?.collections;

      if (!changedCollections || !Array.isArray(allCollections)) return;

      Object.entries(changedCollections).forEach(([indexStr, changedRow]) => {
        const index = Number(indexStr);
        const current = allCollections[index];
        const row = changedRow as ChangedCollectionRow;

        if (!current) return;

        const quantity =
          row.quantity !== undefined ? row.quantity : current.quantity;

        const typeId =
          row.collectionType !== undefined
            ? Number(row.collectionType)
            : Number(current.collectionType);

        if (quantity == null || typeId == null) return;

        const typeData = Object.values(collectionType).find(
          (c: any) => Number(c.id) === typeId,
        );

        if (!typeData) return;

        const newWeight = quantity * (typeData.weight ?? 0);
        form.setFieldValue(['collections', index, 'weight'], newWeight);
      });
    },
    [collectionType, form],
  );

  const handleSubmit = async (values: any, forceSubmit = false) => {
    setLoading(true);
    try {
      const payload = {
        siteId: Number(values.siteId),
        createdAt: values.createdAt?.valueOf(),
        collections: values.collections.map((item: any) => ({
          ...item,
          quantity: Number(item.quantity),
          weight: Number(item.weight),
          collectionType: item.collectionType,
          note: item.note || '',
        })),
        forceSubmit,
      };

      const response = await connection.post(
        `collection/${businessId}`,
        payload,
      );

      // Check if this is a duplicate and user hasn't forced submission
      if (response.data.isDuplicate && !forceSubmit) {
        setLoading(false);
        Modal.confirm({
          title: 'Duplicate Collection Detected',
          content:
            'A collection has already been added for this store today. Do you want to add another collection?',
          okText: 'Continue',
          cancelText: 'Cancel',
          okButtonProps: {
            style: { backgroundColor: '#26a3a7', color: '#fff' },
          },
          onOk: () => {
            handleSubmit(values, true);
          },
        });
        return;
      }

      events.emit('COLLECTION', {
        action: 'CREATE',
        data: { ...response.data },
      });
      message.success('Collection created successfully');
      handleCancel();
    } catch (error) {
      console.error('Error creating collection:', error);
      message.error('Failed to create collection');
    } finally {
      setLoading(false);
    }
  };

  const disableFutureDates = useCallback((current: Dayjs) => {
    return current ? current.endOf('day').isAfter(dayjs()) : false;
  }, []);

  const disableFutureTimeForToday = useCallback((current: Dayjs | null) => {
    if (!current) return {};

    const now = dayjs();
    if (!current.isSame(now, 'day')) return {};

    const hour = now.hour();
    const minute = now.minute();
    const second = now.second();

    return {
      disabledHours: () => range(hour + 1, 23),
      disabledMinutes: (selectedHour: number) =>
        selectedHour === hour ? range(minute + 1, 59) : [],
      disabledSeconds: (selectedHour: number, selectedMinute: number) =>
        selectedHour === hour && selectedMinute === minute
          ? range(second + 1, 59)
          : [],
    };
  }, []);

  return (
    <Modal
      title=" Add Collection"
      open={visible}
      onOk={form.submit}
      onCancel={handleCancel}
      width={800}
      okText="Submit"
      confirmLoading={loading}
      closable={!loading}
      maskClosable={!loading}
      cancelButtonProps={{ disabled: loading }}
      okButtonProps={{
        disabled: loading || !collections || collections.length === 0,
      }}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ collections: [], createdAt: dayjs() }}
        onValuesChange={handleChangeValues}
        onFinish={handleSubmit}
      >
        {/* Site Selector */}
        <Form.Item
          name="siteId"
          label="Select Site"
          rules={[{ required: true, message: 'Please select a site' }]}
        >
          <Select
            placeholder="Choose a site"
            options={siteOptions}
            showSearch
            optionFilterProp="label"
            notFoundContent={siteEmptyHint}
            onChange={(value) => {
              setSelectedSite(siteOptions.find((v) => v.value === value));
            }}
          />
        </Form.Item>

        <Form.Item
          name="createdAt"
          label="Created Date & Time"
          rules={[
            { required: true, message: 'Please select created date and time' },
          ]}
        >
          <DatePicker
            showTime
            format="YYYY/MM/DD HH:mm"
            placeholder="Select date and time"
            className="w-full"
            disabledDate={disableFutureDates}
            disabledTime={disableFutureTimeForToday}
          />
        </Form.Item>

        {/* Store Details Display */}
        {selectedSiteDetails && (
          <div className="mb-4 text-sm text-gray-600">
            <strong>Address:</strong> {selectedSiteDetails.address || 'N/A'} |
            <strong> Suburb:</strong> {selectedSiteDetails.suburb || 'N/A'} |
            <strong> State:</strong> {selectedSiteDetails.state || 'N/A'}
          </div>
        )}

        {/* Collection Items */}
        <Form.List name="collections">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div
                  key={key}
                  className="p-4 bg-gray-50 rounded-md border border-gray-200 mb-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-700">
                      Collection Item #{name + 1}
                    </h4>
                    <MinusCircleOutlined
                      className="text-red-500 text-xl cursor-pointer"
                      onClick={() => remove(name)}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Form.Item
                      {...restField}
                      name={[name, 'collectionType']}
                      label="Collection Type"
                      rules={[
                        {
                          required: true,
                          message: 'Please select collection type',
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select collection type"
                        options={collectionTypes}
                        notFoundContent={collectionTypeEmptyHint}
                      />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, 'quantity']}
                      label="Quantity"
                      rules={[
                        {
                          required: true,
                          message: 'Please enter quantity',
                        },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        className="w-full"
                        placeholder="Quantity"
                      />
                    </Form.Item>

                    <Form.Item
                      {...restField}
                      name={[name, 'weight']}
                      label="Weight (kg)"
                      dependencies={['quantity']}
                      rules={[
                        { required: true, message: 'Please enter weight' },
                      ]}
                    >
                      <InputNumber
                        min={0}
                        step={0.1}
                        className="w-full"
                        placeholder="Enter weight in kg"
                      />
                    </Form.Item>
                  </div>
                  <Form.Item {...restField} name={[name, 'note']} label="Note">
                    <Input.TextArea
                      rows={2}
                      placeholder="Enter notes for this collection item..."
                    />
                  </Form.Item>
                </div>
              ))}

              <Form.Item>
                <Button
                  type="dashed"
                  onClick={() =>
                    add({
                      collectionType: undefined,
                      quantity: 0,
                      weight: 0,
                      note: '',
                    })
                  }
                  block
                  icon={<PlusOutlined />}
                  disabled={loading}
                >
                  Add Collection
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export const useCollectionFormModal = () =>
  useRef<CollectionFormModalInstance>(null);

export default React.forwardRef(CollectionFormModal);
